import { createSignal, JSXElement, Match, Switch } from 'solid-js';
import { TriangleAlertIcon } from 'lucide-solid';
import { Localized } from '~/i18n';
import {
  BillingTabTypes,
  getTranslationKey,
  isFreeQuotaExceededWithoutPaymentMethod,
  isInBillingPage,
  isPaymentOverdue,
  openCustomerPortal
} from '~/pages/Billing/utils.ts';
import { useAppData } from '~/components/AppData';
import Link from '~/components/Link';
import {
  AnyAppPath,
  getSlug,
  replacePathParams,
  WorkspacePagePath
} from '~/components/AppRouter/utils.ts';
import { Button } from '~/components/ui/Button';
import { useLocation, useNavigate } from '@solidjs/router';
import logger from '~/utils/logger';
import { useToast } from '~/components/ui/Toast';
import { getLocalizedString } from '~/i18n/utils.ts';
import { useIntl } from '@cookbook/solid-intl';

export default function InProductBillingBanner(): JSXElement {
  const appData = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const toaster = useToast();
  const intl = useIntl();

  const [isCtaLoading, setIsCtaLoading] = createSignal(false);

  return (
    <div class={`w-full flex items-center gap-2`}>
      <Switch>
        <Match when={isPaymentOverdue(appData.workspace!)}>
          <TriangleAlertIcon
            width={16}
            class={`text-[var(--colors-red-light-9)]`}
          />
          <Localized
            translationKey={getTranslationKey(`payment_overdue_warning`)}
            vars={{
              link: (str: string) => (
                <Link
                  href={replacePathParams(
                    getSlug.workspace(
                      WorkspacePagePath.WORKSPACE_BILLING,
                      true
                    ) as AnyAppPath,
                    {
                      tabType: BillingTabTypes.DETAILS
                    }
                  )}
                >
                  {str}
                </Link>
              )
            }}
          />
          <Button
            size={'xs'}
            class={`ml-auto`}
            role={'link'}
            loading={isCtaLoading()}
            onClick={async () => {
              if (isInBillingPage({ location })) {
                setIsCtaLoading(true);
                try {
                  await openCustomerPortal({
                    workspaceId: appData.workspace!.id
                  });
                } catch (e) {
                  toaster.toast().error({
                    description: getLocalizedString(
                      getTranslationKey(
                        `pay_bill_portal_link_generation_failure_toast`
                      ),
                      { intl }
                    )
                  });
                  logger.error(
                    `InProductBillingBanner: failed to open customer portal`,
                    e
                  );
                } finally {
                  setIsCtaLoading(false);
                }
                return;
              }
              navigate(
                replacePathParams(
                  getSlug.workspace(
                    WorkspacePagePath.WORKSPACE_BILLING,
                    true
                  ) as AnyAppPath,
                  {
                    tabType: BillingTabTypes.DETAILS
                  }
                )
              );
            }}
          >
            <Localized
              translationKey={getTranslationKey('banner_cta_pay_bill')}
            />
          </Button>
        </Match>
        <Match
          when={isFreeQuotaExceededWithoutPaymentMethod(appData.workspace!)}
        >
          <TriangleAlertIcon
            width={16}
            class={`text-[var(--colors-amber-light-9)]`}
          />
          <Localized
            translationKey={getTranslationKey(`free_quota_exceeded_warning`)}
            vars={{
              link: (str: string) => (
                <Link
                  class={`text-link-primary underline`}
                  href={replacePathParams(
                    getSlug.workspace(
                      WorkspacePagePath.WORKSPACE_BILLING,
                      true
                    ) as AnyAppPath,
                    { tabType: BillingTabTypes.DETAILS }
                  )}
                >
                  {str}
                </Link>
              )
            }}
          />
        </Match>
      </Switch>
    </div>
  );
}
